import type { BaseMessage, JwtMessage } from '@hover/embed/post-message-api';

import { Route } from 'src/shared/route';

import { isMessageOfType } from '../types';

/**
 * Requests an access token from the top-level origin. This is intended to be used in iframes.
 * This is needed if the iframe is not able to access the parent window's local storage in
 * order to retrieve the access token and log the user in.
 *
 * @param {function(Token): Promise<void>} onReceived - Callback function to handle the JWT once it is received.
 * @param {function(): void} onRejected - Callback function to handle the case when the popup is blocked or the token is not received.
 */

export const requestTokenFromPopup = (
  onReceived: (jwt: string) => Promise<void>,
  onRejected: () => void,
): void => {
  const url = Route.EMBED_AUTH_CHECK.path;

  const handleJwtMessage = (event: MessageEvent<BaseMessage>) => {
    if (event.origin !== window.location.origin) {
      return;
    }

    if (isMessageOfType<JwtMessage>(event, 'jwt')) {
      const { jwt } = event.data?.payload;

      if (jwt) {
        void onReceived(jwt);
      } else {
        onRejected();
      }
      window.removeEventListener('message', handleJwtMessage);
    }
  };

  const popup = window.open(url, '_blank', 'width=520,height=400');

  // A null response indicates that the popup was blocked. In this case, we can't
  // do anything else with the top level token and will need to prompt the user to
  // log in with a password instead.
  if (!popup) {
    onRejected();
    return;
  }

  window.addEventListener('message', handleJwtMessage);
};
